import React from 'react';
import { TodoListItem } from '@unmind/design-system';
import { OnboardingStatus } from 'Assistant/__generated__/client.schemas';
import { useConversationSummary } from 'Home/PersonalisedToday/hooks/useConversationSummary';
import { useTodoListItemTracking } from 'Home/PersonalisedToday/hooks/useTodoListItemTracking';
import { Link } from 'react-router-dom';
import { isToday, intlFormat, subDays, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import RoutePath from 'App/RoutePath';
import { SkeletonItem } from './SkeletonItem';

export const ChatItem = () => {
  const { t: translate, i18n } = useTranslation('homepage', {
    keyPrefix: 'todo_list_section.chat_item',
  });
  const { summary, loading } = useConversationSummary();

  const onboardingStatus = summary?.onboardingStatus;
  const onboardingCompletedAt = summary?.onboardingCompletedAt;
  const lastMessageAt = summary?.lastMessageAt ?? null;

  const userHasOnboarded =
    onboardingStatus === OnboardingStatus.completed ||
    onboardingStatus === OnboardingStatus.skipped;
  const onboardingCompletedToday = !!(
    onboardingCompletedAt && isToday(onboardingCompletedAt)
  );
  const lastMessageIsToday = !!(lastMessageAt && isToday(lastMessageAt));

  // Mirrors the `completed` each branch below renders.
  const isOnboardingRow = !userHasOnboarded || onboardingCompletedToday;
  const { trackClicked } = useTodoListItemTracking('chat', {
    shown: !!summary,
    completed: isOnboardingRow ? onboardingCompletedToday : lastMessageIsToday,
  });

  if (loading && !summary) {
    return <SkeletonItem />;
  }

  if (!summary) {
    return null;
  }

  if (isOnboardingRow) {
    const translationKey =
      onboardingStatus === OnboardingStatus.inProgress
        ? 'in_progress'
        : 'not_started';

    return (
      <TodoListItem
        description={translate(`onboarding.${translationKey}.description`)}
        cta={translate(`onboarding.${translationKey}.cta`)}
        titleAsChild
        thumbnail={{ icon: 'welcome', tone: 'primary' }}
        completed={onboardingCompletedToday}
      >
        <Link to={RoutePath.Chatbot} onClick={trackClicked}>
          {translate(`onboarding.${translationKey}.title`)}
        </Link>
      </TodoListItem>
    );
  }

  // has the user chatted in the last 7 days
  const isRecent =
    lastMessageAt && subDays(startOfDay(new Date()), 7) < lastMessageAt;
  const translationKey = lastMessageIsToday
    ? 'today'
    : isRecent
      ? 'recent'
      : 'stale';

  const lastMessage = lastMessageAt
    ? {
        day: intlFormat(
          lastMessageAt,
          { weekday: 'long' },
          { locale: i18n.language },
        ),
        time: intlFormat(
          lastMessageAt,
          { hour: 'numeric', minute: 'numeric' },
          { locale: i18n.language },
        ),
      }
    : undefined;

  return (
    <TodoListItem
      description={translate(
        `continue.${translationKey}.description`,
        lastMessage,
      )}
      cta={translate(`continue.${translationKey}.cta`)}
      titleAsChild
      thumbnail={{ icon: 'chat-continue', tone: 'primary' }}
      completed={lastMessageIsToday}
    >
      <Link to={RoutePath.Chatbot} onClick={trackClicked}>
        {translate(`continue.${translationKey}.title`)}
      </Link>
    </TodoListItem>
  );
};
